import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

/** Ein Projekt aus Daniels Projekt-Cockpit (Tabelle cockpit_projekte, gepflegt vom Abendabgleich). */
export interface CockpitProjekt {
  slug: string;
  titel: string;
  bereich: string;
  aktiv: boolean;
}

/** Reihenfolge der Gruppen im Auswahlfeld. */
export const PROJEKT_BEREICHE = ["MusiKonzept", "Übergreifend", "Edukonzept", "Kursbase", "Privat"];

/**
 * Alle Projekte, auch inaktive (damit eine Pille an einer alten Aufgabe ihren Namen behält).
 * Auswahlfelder zeigen nur aktive. Lesen dürfen nur Admins (RLS).
 */
export function useCockpitProjekte() {
  return useQuery({
    queryKey: ["cockpit-projekte"],
    queryFn: async (): Promise<CockpitProjekt[]> => {
      const { data, error } = await supabase
        .from("cockpit_projekte")
        .select("slug, titel, bereich, aktiv")
        .order("titel");
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 10 * 60 * 1000,
  });
}

/** Aktive Projekte nach Bereich gruppiert, in der Reihenfolge von PROJEKT_BEREICHE. */
export function projekteNachBereich(projekte: CockpitProjekt[]): { bereich: string; projekte: CockpitProjekt[] }[] {
  const aktiv = projekte.filter((p) => p.aktiv);
  const bereiche = [...PROJEKT_BEREICHE, ...new Set(aktiv.map((p) => p.bereich))].filter(
    (b, i, alle) => alle.indexOf(b) === i,
  );
  return bereiche
    .map((bereich) => ({ bereich, projekte: aktiv.filter((p) => p.bereich === bereich) }))
    .filter((g) => g.projekte.length > 0);
}

/** Name eines Projekts zum Slug, sonst der Slug selbst. */
export function projektTitel(projekte: CockpitProjekt[], slug: string | null | undefined): string | null {
  if (!slug) return null;
  return projekte.find((p) => p.slug === slug)?.titel ?? slug;
}
